import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, ScrollView, TouchableOpacity, TextInput } from 'react-native';
import LoadingSpinner from './LoadingSpinner';
import { styles } from './sgpCacheTabStyles';
import { SPORT_CATEGORIES, AVAILABLE_TICKER_OPTIONS, getLeaguesBySport } from '../context/TickerContext';
import { useSGPCacheData } from '../hooks/useSGPCacheData';
import { uiVar } from './floodlight/uiStyleCss';

/**
 * SGP Cache Tab
 * 
 * Combines generation and viewing:
 * - Browse all fixtures with cached SGPs
 * - Generate SGPs for any fixture
 * - View cached SGP details in drill-down format
 * - Real-time generation status
 */
const SGPCacheTab = () => {
  const { fixtures, cachedSGPs, selectedLeagues, setSelectedLeagues, searchQuery, setSearchQuery, loading, generating, generatingAll, expandedFixtures, expandedSGPs, generationLogs, bulkProgress, collapsedSections, setCollapsedSections, countdown, sgpCacheStats, sgpHealth, sgpHealthExpanded, setSgpHealthExpanded, leagues, fetchSGPCacheStats, fetchSgpHealth, fetchFixtures, fetchAllCachedSGPs, generateSGPs, triggerFullLeagueRefresh, toggleFixture, toggleSGP, ALL_SGP_SPORTS, MULTILEG_SPORTS } = useSGPCacheData();
  
  // Get game status for color coding (defined before filteredFixtures to avoid hoisting issues)
  const getGameStatus = (startDate) => {
    if (!startDate) return { status: 'upcoming', flag: '🔵', color: '#3B82F6' };
    
    try {
      const gameTime = startDate._seconds 
        ? new Date(startDate._seconds * 1000) 
        : new Date(startDate);
      const now = new Date();
      const diffHours = (gameTime - now) / (1000 * 60 * 60);
      
      if (diffHours < -3) return { status: 'past', flag: '⚪', color: '#4B5563' };
      if (diffHours < 0) return { status: 'live', flag: '🟢', color: '#10B981' };
      if (diffHours < 2) return { status: 'live_soon', flag: '🟠', color: '#F59E0B' };
      return { status: 'upcoming', flag: '🔵', color: '#3B82F6' };
    } catch {
      return { status: 'upcoming', flag: '🔵', color: '#3B82F6' };
    }
  };
  
  // ⭐ Get date group for categorizing fixtures into sections
  const getDateGroup = (startDate) => {
    if (!startDate) return { key: 'unknown', label: "Unknown Date's Fixtures", order: 99, emoji: '❓' };
    
    try {
      const gameTime = startDate._seconds 
        ? new Date(startDate._seconds * 1000) 
        : new Date(startDate);
      
      const now = new Date();
      
      // Reset times to midnight for day comparison
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const gameDay = new Date(gameTime.getFullYear(), gameTime.getMonth(), gameTime.getDate());
      
      const diffDays = Math.floor((gameDay - today) / (1000 * 60 * 60 * 24));
      
      // Live games (started but not more than 3 hours ago)
      if (gameTime < now && gameTime > new Date(now.getTime() - 3 * 60 * 60 * 1000)) {
        return { key: 'live', label: "🟢 Live Now", order: -1, emoji: '🟢' };
      }
      
      // Today
      if (diffDays === 0) {
        return { key: 'today', label: "Today's Fixtures", order: 0, emoji: '🔥' };
      }
      
      // Tomorrow
      if (diffDays === 1) {
        return { key: 'tomorrow', label: "Tomorrow's Fixtures", order: 1, emoji: '📅' };
      }
      
      // This week (2-6 days from now) - show day names
      if (diffDays >= 2 && diffDays <= 6) {
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const dayName = dayNames[gameDay.getDay()];
        return { 
          key: `day_${diffDays}`, 
          label: `${dayName}'s Fixtures`, 
          order: diffDays, 
          emoji: '📆' 
        };
      }
      
      // Next week (7-13 days)
      if (diffDays >= 7 && diffDays <= 13) {
        return { key: 'next_week', label: "Next Week's Fixtures", order: 10, emoji: '📅' };
      }
      
      // Next month (14-30 days)
      if (diffDays >= 14 && diffDays <= 30) {
        return { key: 'next_month', label: "Next Month's Fixtures", order: 20, emoji: '🗓️' };
      }
      
      // Future (31+ days)
      return { key: 'future', label: "Future Fixtures", order: 30, emoji: '🔮' };
      
    } catch {
      return { key: 'unknown', label: "Unknown Date's Fixtures", order: 99, emoji: '❓' };
    }
  };
  
  // Toggle section collapse
  const toggleSection = (sectionKey) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };
  
  // Calculate available SGP combinations based on SGP-FRIENDLY markets only
  const calculateAvailableCombos = (fixture) => {
    if (!fixture.featured_markets && !fixture.markets) {
      return { twoLeg: 0, threeLeg: 0, sgpMarkets: 0, totalOdds: 0 };
    }
    
    // Get all markets
    const markets = fixture.featured_markets || fixture.markets || {};
    
    // Filter to SGP-friendly markets (full game only, no quarters/halves)
    const sgpFriendlyMarkets = Object.entries(markets).filter(([marketKey, marketData]) => {
      if (!marketData || (!marketData.data?.length && !marketData.odds_count)) {
        return false;
      }
      
      // Get market name (use market_name if available, otherwise the key)
      const marketName = (marketData.market_name || marketKey || '').toLowerCase();
      
      // Exclude partial game markets
      if (marketName.includes('1st') || 
          marketName.includes('2nd') || 
          marketName.includes('3rd') || 
          marketName.includes('4th') || 
          marketName.includes('half') || 
          marketName.includes('quarter')) {
        return false;
      }
      
      return true;
    });
    
    const sgpMarketCount = sgpFriendlyMarkets.length;
    
    // Count total odds across SGP-friendly markets only
    let totalOdds = 0;
    sgpFriendlyMarkets.forEach(([marketKey, marketData]) => {
      totalOdds += marketData.data?.length || marketData.odds_count || 0;
    });
    
    // Calculate combinations using actual SGP-friendly odds
    // 2-leg: C(n, 2) = n * (n-1) / 2
    // 3-leg: C(n, 3) = n * (n-1) * (n-2) / 6
    // Cap at backend limits to show realistic generation counts
    const maxFor2Leg = 20; // Backend generates up to 20 choose 2 = 190 combos
    const maxFor3Leg = 15; // Backend generates up to 15 choose 3 = 455 combos
    
    // Use actual odds count (don't artificially cap the display number)
    const n2 = totalOdds; // Show actual potential
    const n3 = totalOdds;
    
    // Calculate combinations
    const twoLeg = n2 >= 2 ? Math.floor((n2 * (n2 - 1)) / 2) : 0;
    const threeLeg = n3 >= 3 ? Math.floor((n3 * (n3 - 1) * (n3 - 2)) / 6) : 0;
    
    // Flag if we'll hit backend limits
    const twoLegCapped = n2 > maxFor2Leg;
    const threeLegCapped = n3 > maxFor3Leg;
    const twoLegActual = twoLegCapped ? Math.floor((maxFor2Leg * (maxFor2Leg - 1)) / 2) : twoLeg;
    const threeLegActual = threeLegCapped ? Math.floor((maxFor3Leg * (maxFor3Leg - 1) * (maxFor3Leg - 2)) / 6) : threeLeg;
    
    return { 
      twoLeg,                    // Total possible combinations
      threeLeg,                  // Total possible combinations
      twoLegActual,              // What backend will actually generate (capped)
      threeLegActual,            // What backend will actually generate (capped)
      twoLegCapped,              // Flag if hitting backend limit
      threeLegCapped,            // Flag if hitting backend limit
      sgpMarkets: sgpMarketCount,
      totalOdds
    };
  };
  
  // Filter fixtures
  const filteredFixtures = useMemo(() => {
    return fixtures.filter(fixture => {
      // League filter - if selectedLeagues is empty, show all
      if (selectedLeagues.length > 0) {
        const fixtureLeague = (fixture.league || '').toLowerCase();
        const matchesLeague = selectedLeagues.some(selectedId => {
          const selectedLower = selectedId.toLowerCase();
          // Direct match
          if (fixtureLeague === selectedLower || fixtureLeague.includes(selectedLower)) return true;
          // Handle common aliases
          if (selectedLower === 'ncaab' && fixtureLeague.includes('ncaa') && fixtureLeague.includes('basketball')) return true;
          if (selectedLower === 'ncaaf' && fixtureLeague.includes('ncaa') && fixtureLeague.includes('football')) return true;
          if (selectedLower === 'epl' && (fixtureLeague.includes('premier') || fixtureLeague.includes('epl'))) return true;
          if (selectedLower === 'champions_league' && fixtureLeague.includes('champions')) return true;
          return false;
        });
        if (!matchesLeague) return false;
      }
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const homeTeam = (fixture.home_team || '').toLowerCase();
        const awayTeam = (fixture.away_team || '').toLowerCase();
        if (!homeTeam.includes(query) && !awayTeam.includes(query)) {
          return false;
        }
      }
      
      return true;
    }).sort((a, b) => {
      // Sort by: LIVE → Upcoming (soonest first) → Past (most recent first)
      const getTimestamp = (fixture) => {
        if (!fixture.start_date) return 0;
        if (fixture.start_date._seconds) return fixture.start_date._seconds * 1000;
        return new Date(fixture.start_date).getTime();
      };
      
      const aTime = getTimestamp(a);
      const bTime = getTimestamp(b);
      const aStatus = getGameStatus(a.start_date);
      const bStatus = getGameStatus(b.start_date);
      
      // Priority 1: LIVE games first
      if (aStatus.status === 'live' && bStatus.status !== 'live') return -1;
      if (bStatus.status === 'live' && aStatus.status !== 'live') return 1;
      
      // Priority 2: Starting soon games
      if (aStatus.status === 'live_soon' && bStatus.status !== 'live_soon' && bStatus.status !== 'live') return -1;
      if (bStatus.status === 'live_soon' && aStatus.status !== 'live_soon' && aStatus.status !== 'live') return 1;
      
      // Priority 3: Upcoming games (soonest first)
      if (aStatus.status === 'upcoming' && bStatus.status === 'upcoming') {
        return aTime - bTime; // Ascending (soonest first)
      }
      
      // Priority 4: Past games (most recent first)
      if (aStatus.status === 'past' && bStatus.status === 'past') {
        return bTime - aTime; // Descending (most recent first)
      }
      
      // Mixed: upcoming before past
      if (aStatus.status === 'upcoming' && bStatus.status === 'past') return -1;
      if (aStatus.status === 'past' && bStatus.status === 'upcoming') return 1;
      
      // Default: by time
      return aTime - bTime;
    });
  }, [fixtures, selectedLeagues, searchQuery]);
  
  // ⭐ Group fixtures by date category
  const groupedFixtures = useMemo(() => {
    const groups = {};
    
    filteredFixtures.forEach(fixture => {
      const dateGroup = getDateGroup(fixture.start_date);
      
      if (!groups[dateGroup.key]) {
        groups[dateGroup.key] = {
          key: dateGroup.key,
          label: dateGroup.label,
          order: dateGroup.order,
          emoji: dateGroup.emoji,
          fixtures: []
        };
      }
      
      groups[dateGroup.key].fixtures.push(fixture);
    });
    
    // Convert to array and sort by order
    return Object.values(groups).sort((a, b) => a.order - b.order);
  }, [filteredFixtures]);
  
  // Format date
  const formatDate = (dateField) => {
    if (!dateField) return 'Unknown date';
    try {
      const date = dateField._seconds 
        ? new Date(dateField._seconds * 1000) 
        : new Date(dateField);
      return date.toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };
  
  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      {/* Header with quick refresh */}
      <View style={[styles.header, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}>
        <View>
          <Text style={styles.headerTitle}>🎯 SGP Cache</Text>
          <Text style={styles.headerSubtitle}>
            Generate & browse cached Same Game Parlays
          </Text>
        </View>
        <TouchableOpacity 
          style={{ 
            backgroundColor: uiVar('adm-card', '#1F2937'), 
            paddingHorizontal: 12, 
            paddingVertical: 8, 
            borderRadius: 8,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6
          }}
          onPress={() => fetchSGPCacheStats()}
          disabled={loading}
        >
          {loading ? (
            <LoadingSpinner size={14} color="#9CA3AF" />
          ) : (
            <Text style={{ color: uiVar('adm-muted', '#9CA3AF'), fontSize: 14 }}>🔄 Refresh Stats</Text>
          )}
        </TouchableOpacity>
      </View>
      
      {/* ⭐ Timer & Stats Card */}
      <View style={styles.timerCard}>
        <View style={styles.timerRow}>
          {/* Countdown Timer */}
          <View style={styles.timerSection}>
            <Text style={styles.timerLabel}>⏱️ Next Auto-Refresh</Text>
            <Text style={styles.timerValue}>{countdown || 'Calculating...'}</Text>
            <Text style={styles.timerSubtext}>Runs every hour at :40</Text>
          </View>
          
          {/* Last SGP Generation */}
          <View style={styles.timerSection}>
            <Text style={styles.timerLabel}>📅 Last Generated</Text>
            <Text style={[styles.timerValue, { color: sgpCacheStats?.timestamps?.last_sgp_generation ? '#10B981' : '#EF4444' }]}>
              {loading ? 'Loading...' : sgpCacheStats?.timestamps?.last_sgp_generation ? (
                (() => {
                  const ageMinutes = sgpCacheStats.timestamps.last_sgp_generation_age_minutes;
                  if (ageMinutes < 60) return `${ageMinutes}m ago`;
                  const hours = Math.floor(ageMinutes / 60);
                  if (hours < 24) return `${hours}h ago`;
                  return new Date(sgpCacheStats.timestamps.last_sgp_generation).toLocaleDateString();
                })()
              ) : 'Never'}
            </Text>
            <Text style={styles.timerSubtext}>
              {loading ? 'Fetching...' : sgpCacheStats?.timestamps?.last_sgp_generation ? (
                new Date(sgpCacheStats.timestamps.last_sgp_generation).toLocaleTimeString()
              ) : 'No SGPs generated yet'}
            </Text>
          </View>
        </View>
        
        {/* Cache Stats Summary - Main numbers */}
        {sgpCacheStats?.aggregate && (
          <View style={styles.cacheStatsSummary}>
            <View style={styles.cacheStatItem}>
              <Text style={styles.cacheStatValue}>{sgpCacheStats.aggregate.total_fixtures_cached || 0}</Text>
              <Text style={styles.cacheStatLabel}>UPCOMING</Text>
              <Text style={{ color: uiVar('adm-muted2', '#6B7280'), fontSize: 9 }}>with SGPs</Text>
            </View>
            <View style={styles.cacheStatItem}>
              <Text style={[styles.cacheStatValue, { color: '#10B981' }]}>
                {(sgpCacheStats.aggregate.total_sgps || 0).toLocaleString()}
              </Text>
              <Text style={styles.cacheStatLabel}>TOTAL SGPs</Text>
            </View>
            <View style={styles.cacheStatItem}>
              <Text style={[styles.cacheStatValue, { color: uiVar('adm-accent', '#8B5CF6') }]}>
                {(sgpCacheStats.aggregate.total_2leg || 0).toLocaleString()}
              </Text>
              <Text style={styles.cacheStatLabel}>2-LEG</Text>
            </View>
            <View style={styles.cacheStatItem}>
              <Text style={[styles.cacheStatValue, { color: '#F59E0B' }]}>
                {(sgpCacheStats.aggregate.total_3leg || 0).toLocaleString()}
              </Text>
              <Text style={styles.cacheStatLabel}>3-LEG</Text>
            </View>
          </View>
        )}
        
        {/* ⭐ Uncached fixtures warning */}
        {sgpCacheStats?.aggregate?.total_fixtures_uncached > 0 && (
          <View style={{ 
            backgroundColor: 'rgba(251, 191, 36, 0.1)', 
            borderRadius: 8, 
            padding: 12, 
            marginTop: 12,
            borderLeftWidth: 3,
            borderLeftColor: '#F59E0B'
          }}>
            <Text style={{ color: '#F59E0B', fontWeight: '600', marginBottom: 4 }}>
              ⏳ {sgpCacheStats.aggregate.total_fixtures_uncached} fixtures need SGP generation
            </Text>
            <Text style={{ color: uiVar('adm-muted', '#9CA3AF'), fontSize: 12 }}>
              Primary sports auto-refresh at :20 & :50. Multi-leg append at :40. Or click "Refresh All Leagues" to run all sports in parallel now.
            </Text>
          </View>
        )}
        
        {/* League Breakdown - now uses by_league from response root */}
        {sgpCacheStats?.by_league && Object.keys(sgpCacheStats.by_league).length > 0 && (
          <View style={styles.leagueBreakdown}>
            <Text style={styles.leagueBreakdownTitle}>SGPs by League:</Text>
            <View style={styles.leagueBreakdownRow}>
              {Object.entries(sgpCacheStats.by_league)
                .filter(([_, data]) => data.sgps > 0)
                .sort((a, b) => b[1].sgps - a[1].sgps)
                .slice(0, 6)
                .map(([league, data]) => (
                  <View key={league} style={styles.leagueBreakdownItem}>
                    <Text style={styles.leagueBreakdownLeague}>{league.toUpperCase()}</Text>
                    <Text style={styles.leagueBreakdownCount}>{data.sgps}</Text>
                  </View>
                ))}
            </View>
          </View>
        )}
      </View>
      
      {/* ⭐ NEW: Automation Health (getSgpHealth) */}
      <View style={[styles.timerCard, { marginTop: 8 }]}>
        <TouchableOpacity
          style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingBottom: sgpHealthExpanded ? 10 : 0 }}
          onPress={() => { setSgpHealthExpanded(e => !e); if (!sgpHealth) fetchSgpHealth(); }}
          activeOpacity={0.7}
        >
          <Text style={[styles.timerLabel, { fontSize: 13, fontWeight: '700', color: uiVar('adm-ink', '#F9FAFB') }]}>
            ⚡ Automation Health
          </Text>
          <Text style={{ color: uiVar('adm-muted2', '#6B7280'), fontSize: 11 }}>{sgpHealthExpanded ? '▲ hide' : '▼ show'}</Text>
        </TouchableOpacity>

        {sgpHealthExpanded && (
          <View>
            {!sgpHealth ? (
              <Text style={{ color: uiVar('adm-muted2', '#6B7280'), fontSize: 12, marginTop: 6 }}>Loading…</Text>
            ) : (
              <View style={{ gap: 8, marginTop: 4 }}>
                {/* Recent Alerts */}
                {Array.isArray(sgpHealth.recentAlerts) && sgpHealth.recentAlerts.length > 0 && (
                  <View style={{ gap: 4 }}>
                    <Text style={[styles.timerLabel, { marginBottom: 2 }]}>🚨 Recent Alerts</Text>
                    {sgpHealth.recentAlerts.slice(0, 3).map((a, i) => (
                      <View key={i} style={{ backgroundColor: a.severity === 'critical' ? '#2D1B1B' : '#1F2917', borderRadius: 6, padding: 8, borderLeftWidth: 3, borderLeftColor: a.severity === 'critical' ? '#EF4444' : '#F59E0B' }}>
                        <Text style={{ color: uiVar('adm-ink', '#F9FAFB'), fontSize: 12, fontWeight: '600' }}>{a.subject}</Text>
                        <Text style={{ color: uiVar('adm-muted', '#9CA3AF'), fontSize: 11, marginTop: 2 }}>{a.message} · {a.age_min}m ago</Text>
                      </View>
                    ))}
                  </View>
                )}

                {/* Per-sport rows */}
                {Object.entries(sgpHealth.health || {}).length > 0 && (
                  <View>
                    <Text style={[styles.timerLabel, { marginBottom: 4 }]}>📊 Sport Status</Text>
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                      {Object.entries(sgpHealth.health).map(([key, h]) => {
                        const sport = key.replace(/^latest_/, '').replace(/_/g, ' ');
                        const ageOk = h.age_min != null && h.age_min < 90;
                        const color = h.timed_out ? '#EF4444' : ageOk ? '#10B981' : '#F59E0B';
                        const retries = sgpHealth.retryQueueSizes?.[sport] || 0;
                        return (
                          <View key={key} style={{ backgroundColor: uiVar('adm-card', '#111827'), borderRadius: 8, padding: 8, borderWidth: 1, borderColor: color + '55', minWidth: 110 }}>
                            <Text style={{ color: uiVar('adm-ink', '#F9FAFB'), fontSize: 11, fontWeight: '700', textTransform: 'capitalize' }}>{sport}</Text>
                            <Text style={{ color, fontSize: 12, fontWeight: '700', marginTop: 2 }}>
                              {h.age_min != null ? `${h.age_min}m ago` : '—'}
                              {h.timed_out ? ' ⏱️' : ''}
                            </Text>
                            <Text style={{ color: uiVar('adm-muted', '#9CA3AF'), fontSize: 10, marginTop: 1 }}>
                              {h.total_sgps != null ? `${h.total_sgps} SGPs` : ''}
                              {h.failed ? ` · ${h.failed} failed` : ''}
                              {retries > 0 ? ` · ${retries} retry` : ''}
                            </Text>
                          </View>
                        );
                      })}
                    </View>
                  </View>
                )}

                <TouchableOpacity onPress={fetchSgpHealth} style={{ alignSelf: 'flex-start', marginTop: 4 }}>
                  <Text style={{ color: uiVar('adm-accent', '#8B5CF6'), fontSize: 12 }}>↻ Refresh</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}
      </View>

      {/* Search */}
      <View style={styles.searchContainer}>
        <TextInput
          style={styles.searchInput}
          placeholder="Search by team..."
          placeholderTextColor="#9CA3AF"
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
      </View>
      
      {/* League Filter - Sport Categories & Individual Leagues */}
      <View style={styles.leagueFilterContainer}>
        {/* Sport Category Quick Toggles */}
        <View style={styles.sportCategoriesRow}>
          <Text style={styles.filterSectionLabel}>Filter by Sport:</Text>
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.sportCategoriesContent}
          >
            {SPORT_CATEGORIES.filter(cat => ['all_basketball', 'all_football', 'all_hockey', 'all_baseball', 'all_soccer'].includes(cat.id)).map((category) => {
              const sportLeagues = getLeaguesBySport(category.sport);
              const selectedCount = sportLeagues.filter(l => selectedLeagues.includes(l)).length;
              const allSelected = selectedCount === sportLeagues.length && selectedCount > 0;
              const someSelected = selectedCount > 0 && !allSelected;
              
              return (
                <TouchableOpacity
                  key={category.id}
                  onPress={() => {
                    if (allSelected) {
                      // Deselect all leagues in this sport
                      setSelectedLeagues(prev => prev.filter(l => !sportLeagues.includes(l)));
                    } else {
                      // Select all leagues in this sport
                      setSelectedLeagues(prev => [...new Set([...prev, ...sportLeagues])]);
                    }
                  }}
                  style={[
                    styles.sportCategoryButton,
                    allSelected && styles.sportCategoryButtonActive,
                    someSelected && styles.sportCategoryButtonPartial
                  ]}
                >
                  <Text style={[
                    styles.sportCategoryText,
                    allSelected && styles.sportCategoryTextActive,
                    someSelected && styles.sportCategoryTextPartial
                  ]}>
                    {category.label.split(' ')[0]} {category.label.split(' ').slice(1).join(' ')}
                    {someSelected ? ` (${selectedCount})` : ''}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
        
        {/* Individual League Toggles */}
        <View style={styles.leagueTogglesRow}>
          <Text style={styles.filterSectionLabel}>Leagues:</Text>
          <ScrollView 
            horizontal 
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.leagueFilterContent}
          >
            {/* Show All button */}
            <TouchableOpacity
              style={[
                styles.leagueButton,
                selectedLeagues.length === 0 && styles.leagueButtonActive
              ]}
              onPress={() => setSelectedLeagues([])}
            >
              <Text style={[
                styles.leagueButtonText,
                selectedLeagues.length === 0 && styles.leagueButtonTextActive
              ]}>
                All
              </Text>
            </TouchableOpacity>
            
            {/* Only show leagues that have fixtures in our data */}
            {leagues.filter(l => l.id !== 'all').map(league => {
              const isSelected = selectedLeagues.includes(league.id);
              return (
                <TouchableOpacity
                  key={league.id}
                  style={[
                    styles.leagueButton,
                    isSelected && styles.leagueButtonActive
                  ]}
                  onPress={() => {
                    if (isSelected) {
                      setSelectedLeagues(prev => prev.filter(l => l !== league.id));
                    } else {
                      setSelectedLeagues(prev => [...prev, league.id]);
                    }
                  }}
                >
                  <Text style={[
                    styles.leagueButtonText,
                    isSelected && styles.leagueButtonTextActive
                  ]}>
                    {league.name}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
        
        {/* Quick Actions */}
        {selectedLeagues.length > 0 && (
          <View style={styles.filterQuickActions}>
            <Text style={styles.filterActiveText}>
              {selectedLeagues.length} league{selectedLeagues.length !== 1 ? 's' : ''} selected
            </Text>
            <TouchableOpacity onPress={() => setSelectedLeagues([])}>
              <Text style={styles.filterClearText}>Clear All</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
      
      {/* Action Buttons */}
      <View style={styles.actionButtonsContainer}>
        <TouchableOpacity 
          style={[styles.actionButton, styles.fetchButton, { backgroundColor: '#3B82F6' }]} 
          onPress={() => { fetchFixtures(); fetchAllCachedSGPs(); fetchSGPCacheStats(); }}
          disabled={loading}
        >
          {loading ? (
            <LoadingSpinner size={16} color="#FFFFFF" />
          ) : (
            <Text style={styles.actionButtonText}>🔄 Refresh Data</Text>
          )}
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={[styles.actionButton, styles.generateAllButton]} 
          onPress={triggerFullLeagueRefresh}
          disabled={generatingAll}
        >
          {generatingAll ? (
            <LoadingSpinner size={16} color="#FFFFFF" />
          ) : (
            <Text style={styles.actionButtonText}>🤖 Refresh All Leagues</Text>
          )}
        </TouchableOpacity>
      </View>
      
      {/* Bulk Generation Progress */}
      {bulkProgress && (
        <View style={styles.progressContainer}>
          <View style={styles.progressHeader}>
            <Text style={styles.progressTitle}>
              {bulkProgress.status === 'starting' ? '🚀 Starting...' : 
               bulkProgress.status === 'complete' ? '✅ All leagues refreshed!' : 
               '⚡ Refreshing all leagues in parallel…'}
            </Text>
            <Text style={styles.progressStats}>
              {bulkProgress.status === 'complete'
                ? `${bulkProgress.successful || 0} jobs done • ${bulkProgress.failed || 0} failed • ${(bulkProgress.totalSGPs || 0).toLocaleString()} total SGPs cached`
                : `${ALL_SGP_SPORTS?.length || 11} sports × 2-leg + ${MULTILEG_SPORTS?.length || 4} sports × multi-leg running simultaneously • ${(bulkProgress.totalSGPs || 0).toLocaleString()} SGPs cached so far`
              }
            </Text>
          </View>
          
          {/* Status message */}
          {bulkProgress.currentFixture && bulkProgress.status === 'running' && (
            <Text style={styles.currentFixture}>
              {bulkProgress.currentFixture}
            </Text>
          )}
          
          {/* Progress Bar */}
          <View style={styles.progressBarContainer}>
            <View style={[styles.progressBarFill, { width: `${bulkProgress.progress_percent || 0}%` }]} />
          </View>
          
          <Text style={styles.progressPercent}>{bulkProgress.progress_percent || 0}%</Text>
          
          {/* Estimated Time */}
          {bulkProgress.status === 'running' && bulkProgress.processed > 0 && (
            <Text style={styles.estimatedTime}>
              ⏱️ ~{Math.ceil((bulkProgress.total - bulkProgress.processed) * 2)} min remaining (avg 2 min/fixture)
            </Text>
          )}
        </View>
      )}
      
      {/* Loading */}
      {loading && (
        <View style={styles.loadingContainer}>
          <LoadingSpinner size={32} color="#3B82F6" />
          <Text style={styles.loadingText}>Loading fixtures...</Text>
        </View>
      )}
      
      {/* Fixtures List - Grouped by Date */}
      {!loading && (
        <View style={styles.fixturesContainer}>
          <Text style={styles.resultsCount}>
            {filteredFixtures.length} fixture{filteredFixtures.length !== 1 ? 's' : ''} available in {groupedFixtures.length} group{groupedFixtures.length !== 1 ? 's' : ''}
          </Text>
          
          {groupedFixtures.map(group => {
            const isCollapsed = collapsedSections[group.key];
            const cachedCount = group.fixtures.filter(f => cachedSGPs[f.fixture_id || f.id]).length;
            
            // Get unique leagues in this group with counts
            const leagueCounts = {};
            group.fixtures.forEach(f => {
              const league = (f.league || 'unknown').toUpperCase();
              leagueCounts[league] = (leagueCounts[league] || 0) + 1;
            });
            const groupLeagues = Object.entries(leagueCounts).sort((a, b) => b[1] - a[1]); // Sort by count desc
            
            return (
              <View key={group.key} style={styles.dateGroupContainer}>
                {/* Section Header */}
                <TouchableOpacity 
                  style={styles.sectionHeader}
                  onPress={() => toggleSection(group.key)}
                >
                  <View style={styles.sectionHeaderLeft}>
                    <Text style={styles.sectionEmoji}>{group.emoji}</Text>
                    <Text style={styles.sectionTitle}>{group.label}</Text>
                    <View style={styles.sectionBadge}>
                      <Text style={styles.sectionBadgeText}>
                        {group.fixtures.length} game{group.fixtures.length !== 1 ? 's' : ''}
                      </Text>
                    </View>
                    {cachedCount > 0 && (
                      <View style={[styles.sectionBadge, styles.sectionBadgeCached]}>
                        <Text style={[styles.sectionBadgeText, { color: '#10B981' }]}>
                          {cachedCount} cached
                        </Text>
                      </View>
                    )}
                  </View>
                  <View style={styles.sectionHeaderRight}>
                    {/* League badges */}
                    <View style={styles.sectionLeagues}>
                      {groupLeagues.slice(0, 5).map(([league, count]) => (
                        <View key={league} style={styles.sectionLeagueBadge}>
                          <Text style={styles.sectionLeagueText}>{league}</Text>
                          <Text style={styles.sectionLeagueCount}>{count}</Text>
                        </View>
                      ))}
                      {groupLeagues.length > 5 && (
                        <Text style={styles.sectionLeagueMore}>+{groupLeagues.length - 5}</Text>
                      )}
                    </View>
                    <Text style={styles.sectionExpandIcon}>{isCollapsed ? '▶' : '▼'}</Text>
                  </View>
                </TouchableOpacity>
                
                {/* Section Content */}
                {!isCollapsed && group.fixtures.map(fixture => {
                  const fixtureId = fixture.fixture_id || fixture.id;
                  const isExpanded = expandedFixtures[fixtureId];
                  const isGenerating = generating[fixtureId];
                  const cached = cachedSGPs[fixtureId];
                  const gameStatus = getGameStatus(fixture.start_date);
                  const combos = calculateAvailableCombos(fixture);
                  
                  return (
                    <View 
                      key={fixtureId} 
                      style={[
                        styles.fixtureCard,
                        { 
                          borderLeftWidth: 4, 
                          borderLeftColor: gameStatus.color,
                          borderStyle: cached ? 'solid' : 'dotted',
                          borderWidth: cached ? 1 : 2
                        }
                      ]}
                    >
                      {/* Fixture Header */}
                      <TouchableOpacity 
                        style={styles.fixtureHeader}
                        onPress={() => toggleFixture(fixture)}
                      >
                        <View style={styles.fixtureHeaderLeft}>
                          <View style={styles.fixtureHeaderTop}>
                            <Text style={styles.fixtureLeague}>{(fixture.league || '').toUpperCase()}</Text>
                            <View style={styles.statusBadge}>
                              <Text style={styles.statusFlag}>{gameStatus.flag}</Text>
                            </View>
                            {cached && (
                              <View style={styles.cachedBadge}>
                                <Text style={styles.cachedBadgeText}>
                                  ✓ {cached.stats?.total_sgps || 0} SGPs
                                </Text>
                              </View>
                            )}
                          </View>
                          <Text style={styles.fixtureTeams}>
                            {fixture.away_team} @ {fixture.home_team}
                          </Text>
                          <Text style={styles.fixtureDate}>{formatDate(fixture.start_date)}</Text>
                          <View style={styles.fixtureStatsRow}>
                            <Text style={styles.fixtureStats}>
                              {fixture.total_markets} markets • {fixture.total_odds_available} odds
                            </Text>
                            {fixture.total_markets && (
                              <View style={[
                                styles.sgpReadyBadge,
                                Math.floor(fixture.total_markets * 0.35) >= 5 ? styles.sgpReadyBadgeGood : styles.sgpReadyBadgeWarn
                              ]}>
                                <Text style={[
                                  styles.sgpReadyBadgeText,
                                  { color: Math.floor(fixture.total_markets * 0.35) >= 5 ? '#10B981' : '#F59E0B' }
                                ]}>
                                  ~{Math.floor(fixture.total_markets * 0.35)} SGP markets
                                </Text>
                              </View>
                            )}
                          </View>
                          
                          {/* Available Combo Counts */}
                          {combos.sgpMarkets > 0 && (
                            <View style={styles.comboCountsRow}>
                              <Text style={styles.comboCountsLabel}>Available Combinations:</Text>
                              <View style={styles.comboCountsValues}>
                                <View style={styles.comboCountBadge}>
                                  <Text style={styles.comboCountLabel}>2-leg:</Text>
                                  <Text style={styles.comboCountValue}>
                                    {combos.twoLeg >= 1000000 
                                      ? `${(combos.twoLeg / 1000000).toFixed(2)}M`
                                      : combos.twoLeg >= 1000 
                                        ? `${(combos.twoLeg / 1000).toFixed(1)}K` 
                                        : combos.twoLeg}
                                  </Text>
                                </View>
                                <View style={styles.comboCountBadge}>
                                  <Text style={styles.comboCountLabel}>3-leg:</Text>
                                  <Text style={styles.comboCountValue}>
                                    {combos.threeLeg >= 1000000 
                                      ? `${(combos.threeLeg / 1000000).toFixed(2)}M`
                                      : combos.threeLeg >= 1000 
                                        ? `${(combos.threeLeg / 1000).toFixed(1)}K` 
                                        : combos.threeLeg}
                                  </Text>
                                </View>
                                <Text style={styles.comboCountsHint}>
                                  ({combos.sgpMarkets} markets, {combos.totalOdds} odds)
                                </Text>
                              </View>
                            </View>
                          )}
                        </View>
                        
                        <View style={styles.fixtureHeaderRight}>
                          <TouchableOpacity
                            style={styles.generateButton}
                            onPress={(e) => {
                              e.stopPropagation();
                              generateSGPs(fixture);
                            }}
                            disabled={isGenerating}
                          >
                            {isGenerating ? (
                              <LoadingSpinner size={14} color="#FFFFFF" />
                            ) : (
                              <Text style={styles.generateButtonText}>
                                {cached ? '🔄 Regenerate' : '✨ Generate'}
                              </Text>
                            )}
                          </TouchableOpacity>
                          <Text style={styles.expandIcon}>{isExpanded ? '▼' : '▶'}</Text>
                        </View>
                      </TouchableOpacity>
                
                      {/* Generation Log */}
                      {generationLogs[fixtureId] && (
                        <View style={styles.logContainer}>
                          <Text style={styles.logText}>{generationLogs[fixtureId]}</Text>
                        </View>
                      )}
                      
                      {/* Expanded: Show SGPs */}
                      {isExpanded && cached && (
                        <View style={styles.sgpsContainer}>
                          {Object.entries(cached.sgps || {}).map(([legCount, sgps]) => {
                            // Ensure sgps is an array
                            const sgpArray = Array.isArray(sgps) ? sgps : [];
                            
                            if (sgpArray.length === 0) return null;
                            
                            return (
                            <View key={legCount} style={styles.legCountSection}>
                              <Text style={styles.legCountHeader}>
                                🎯 {legCount} ({sgpArray.length} SGPs)
                              </Text>
                              
                              {sgpArray.slice(0, 10).map((sgp, index) => {
                                const sgpKey = `${fixtureId}_${legCount}_${index}`;
                                const sgpExpanded = expandedSGPs[sgpKey];
                                
                                return (
                                  <View key={index} style={styles.sgpCard}>
                                    <TouchableOpacity
                                      style={styles.sgpHeader}
                                      onPress={() => toggleSGP(fixtureId, legCount, index)}
                                    >
                                      <View style={styles.sgpHeaderLeft}>
                                        <Text style={styles.sgpRank}>#{index + 1}</Text>
                                        <View>
                                          <Text style={styles.sgpOdds}>
                                            {sgp.combined_price > 0 ? '+' : ''}{sgp.combined_price}
                                          </Text>
                                          <Text style={styles.sgpBook}>@ {sgp.best_book}</Text>
                                        </View>
                                      </View>
                                      <View style={styles.sgpHeaderRight}>
                                        <View style={[styles.evBadge, sgp.ev_percent > 0 ? styles.evBadgePositive : null]}>
                                          <Text style={styles.evBadgeText}>
                                            {sgp.ev_source === 'calculated' && '⚠️ '}
                                            {sgp.ev_source === 'api' && '✓ '}
                                            {sgp.ev_percent > 0 ? '+' : ''}{sgp.ev_percent.toFixed(1)}% EV
                                          </Text>
                                        </View>
                                        <Text style={styles.expandIcon}>{sgpExpanded ? '▼' : '▶'}</Text>
                                      </View>
                                    </TouchableOpacity>
                                    
                                    {/* Expanded: Show Legs */}
                                    {sgpExpanded && (
                                      <View style={styles.legsContainer}>
                                        {/* AI Fair Price & Correlation */}
                                        {(sgp.ai_fair_price || sgp.correlation_factor) && (
                                          <View style={styles.aiDataContainer}>
                                            {sgp.ai_fair_price && (
                                              <View style={styles.aiDataRow}>
                                                <Text style={styles.aiDataLabel}>🤖 AI Fair Price:</Text>
                                                <Text style={styles.aiDataValue}>
                                                  {sgp.ai_fair_price > 0 ? '+' : ''}{sgp.ai_fair_price}
                                                </Text>
                                              </View>
                                            )}
                                            {sgp.correlation_factor !== null && sgp.correlation_factor !== undefined && (
                                              <View style={styles.aiDataRow}>
                                                <Text style={styles.aiDataLabel}>
                                                  🔗 Correlation:
                                                  {sgp.has_correlation ? ' ✓' : ' ⚠️'}
                                                </Text>
                                                <Text style={styles.aiDataValue}>
                                                  {(sgp.correlation_factor * 100).toFixed(0)}%
                                                </Text>
                                              </View>
                                            )}
                                          </View>
                                        )}
                                        
                                        <View style={styles.baselineRow}>
                                          <Text style={styles.baselineLabel}>Baseline:</Text>
                                          <Text style={styles.baselineValue}>
                                            {sgp.fair_price > 0 ? '+' : ''}{sgp.fair_price || 'N/A'} @ {sgp.baseline_book || 'DraftKings'}
                                          </Text>
                                        </View>
                                        
                                        {(sgp.legs || []).map((leg, legIndex) => (
                                          <View key={legIndex} style={styles.legRow}>
                                            <Text style={styles.legNumber}>Leg {legIndex + 1}</Text>
                                            <View style={styles.legDetails}>
                                              <Text style={styles.legMarket}>{leg.market_name || leg.market}</Text>
                                              <Text style={styles.legSelection}>
                                                {leg.selection} {leg.selection_line || ''}
                                              </Text>
                                              {leg.price && (
                                                <Text style={styles.legPrice}>
                                                  {leg.price > 0 ? '+' : ''}{leg.price}
                                                </Text>
                                              )}
                                            </View>
                                          </View>
                                        ))}
                                        
                                        {Array.isArray(sgp.all_books) && sgp.all_books.length > 1 && (
                                          <View style={styles.allBooksContainer}>
                                            <Text style={styles.allBooksHeader}>All Books:</Text>
                                            {sgp.all_books.map((book, bookIndex) => (
                                              <View key={bookIndex} style={styles.bookRow}>
                                                <Text style={styles.bookName}>{book.sportsbook}</Text>
                                                <View style={styles.bookPriceContainer}>
                                                  <Text style={styles.bookPrice}>
                                                    {book.price > 0 ? '+' : ''}{book.price}
                                                  </Text>
                                                  {book.ev_percent !== undefined && book.ev_percent !== null && (
                                                    <Text style={[
                                                      styles.bookEV,
                                                      book.ev_percent > 0 ? styles.bookEVPositive : styles.bookEVNegative
                                                    ]}>
                                                      ({book.ev_source === 'api' ? '✓' : '⚠️'} {book.ev_percent > 0 ? '+' : ''}{book.ev_percent.toFixed(1)}% EV)
                                                    </Text>
                                                  )}
                                                </View>
                                              </View>
                                            ))}
                                          </View>
                                        )}
                                      </View>
                                    )}
                                  </View>
                                );
                              })}
                              
                              {sgpArray.length > 10 && (
                                <Text style={styles.moreText}>
                                  + {sgpArray.length - 10} more {legCount} SGPs
                                </Text>
                              )}
                            </View>
                            );
                          })}
                          
                          {/* Generation Stats */}
                          {cached.stats && (
                            <View style={styles.statsContainer}>
                              <Text style={styles.statsHeader}>📊 Generation Stats:</Text>
                              <View style={styles.statsGrid}>
                                {/* ⭐ NEW: Show potential combinations (the big impressive number) */}
                                {(cached.stats.potential_combinations || cached.stats.combinations_checked) && (
                                  <View style={styles.statItem}>
                                    <Text style={styles.statLabel}>🧮 Potential Combinations:</Text>
                                    <Text style={[styles.statValue, { color: '#10B981', fontWeight: 'bold' }]}>
                                      {(cached.stats.potential_combinations || cached.stats.combinations_checked).toLocaleString()}
                                    </Text>
                                  </View>
                                )}
                                {cached.stats.props_found && (
                                  <View style={styles.statItem}>
                                    <Text style={styles.statLabel}>Player Props Found:</Text>
                                    <Text style={styles.statValue}>{cached.stats.props_found.toLocaleString()}</Text>
                                  </View>
                                )}
                                {cached.stats.unique_players && (
                                  <View style={styles.statItem}>
                                    <Text style={styles.statLabel}>Unique Players:</Text>
                                    <Text style={styles.statValue}>{cached.stats.unique_players}</Text>
                                  </View>
                                )}
                                {(cached.stats.markets_found || cached.stats.markets_used) && (
                                  <View style={styles.statItem}>
                                    <Text style={styles.statLabel}>Markets Used:</Text>
                                    <Text style={styles.statValue}>{cached.stats.markets_found || cached.stats.markets_used}</Text>
                                  </View>
                                )}
                                {cached.stats.total_sgps && (
                                  <View style={styles.statItem}>
                                    <Text style={styles.statLabel}>Best SGPs Selected:</Text>
                                    <Text style={styles.statValue}>{cached.stats.total_sgps}</Text>
                                  </View>
                                )}
                              </View>
                            </View>
                          )}
                          
                          {cached.generated_at && (
                            <Text style={styles.generatedAt}>
                              Generated: {new Date(cached.generated_at).toLocaleString()}
                            </Text>
                          )}
                        </View>
                      )}
                
                      {/* No Cache Message */}
                      {isExpanded && !cached && !isGenerating && (
                        <View style={styles.noCacheContainer}>
                          <Text style={styles.noCacheText}>
                            No cached SGPs yet. Click "Generate" to create them.
                          </Text>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            );
          })}
        </View>
      )}
    </ScrollView>
  );
};


export default SGPCacheTab;
